/*
 * Workspaces Page Panel Template
 *
 * Includes:
 * - Workspaces Page
 */

import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  WorkspacesPanelArea,
} from '../types';
import {
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  MEDIUM_NAV_MIN_WIDTH,
} from './constants';

/**
 * Base template (Mobile +)
 */
export function baseTemplate(): PanelsBreakpointTemplate {
  return {
    areas: [['directoryNav', 'directoryContent']],
    resizers: {},
    columns: [
      /* Workspaces Nav */
      {
        key: 'directoryNav',
        defaultMin: MEDIUM_NAV_MIN_WIDTH,
        defaultSize: MEDIUM_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Workspaces Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: PanelArea[]
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent']],
    resizers: {
      directoryNav: 'right',
    },
    columns: [
      /* Workspaces Nav */
      {
        key: 'directoryNav',
        defaultMin: MEDIUM_NAV_MIN_WIDTH,
        defaultSize: MEDIUM_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_DEFAULT_WIDTH,
        isToggleable: true,
        showing: visible.includes('directoryNav'),
      },

      /* Workspaces Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Large template
 */
export const largeTemplate = (
  visible: PanelArea[]
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent']],
    resizers: {
      directoryNav: 'right',
    },
    columns: [
      /* Workspaces Nav */
      {
        key: 'directoryNav',
        defaultMin: MEDIUM_NAV_MIN_WIDTH,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        showing: visible.includes('directoryNav'),
      },

      /* Workspaces Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

export const getDefaultVisiblePanels = (): Array<WorkspacesPanelArea> => {
  return ['directoryContent'];
};

export const getMultiWorkspaceVisiblePanels =
  (): Array<WorkspacesPanelArea> => {
    return ['directoryNav', 'directoryContent'];
  };

export function getPanelState(visible: PanelArea[]): PanelsTemplate {
  return {
    base: baseTemplate(),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}
